import type { PlanogramScene } from "./types";
import type { ViewportPoint } from "./viewport";

/**
 * Where two neighbouring bays meet on the drawing: the place a merchant
 * breaks the aisle, or joins the bays again.
 *
 * There is one seam per pair of bays, so never one after the last bay — the
 * run already ends there.
 */
export interface PlanogramSeam {
  /** The bay on the left; a break is carried by this bay (`splitAfter`). */
  bayId: string;
  /** That bay's 1-based number; the bay on the right is `bayNo + 1`. */
  bayNo: number;
  /** True when the aisle already breaks here, so the seam is the drawn gap. */
  isBreak: boolean;
  /** World x of the seam: the bays' shared edge, or the middle of the gap. */
  x: number;
  /** The world-space band a pointer has to be in to be "on" the seam. */
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Every seam of the scene, left to right. `halfWidth` is how far either side
 * of a shared edge still counts as the seam (world units); a break's band is
 * its whole gap plus that much.
 */
export const getPlanogramSeams = (
  scene: Pick<PlanogramScene, "bays" | "splits">,
  halfWidth: number,
): PlanogramSeam[] => {
  const seams: PlanogramSeam[] = [];

  for (let index = 0; index < scene.bays.length - 1; index += 1) {
    const bay = scene.bays[index];
    const nextBay = scene.bays[index + 1];
    if (!bay || !nextBay) continue;

    const split = scene.splits.find((candidate) => candidate.bayId === bay.id);
    const edge = bay.x + bay.width;
    const top = Math.min(bay.y, nextBay.y);
    const bottom = Math.max(bay.y + bay.height, nextBay.y + nextBay.height);

    seams.push(
      split
        ? {
            bayId: bay.id,
            bayNo: bay.bayNo,
            bottom,
            isBreak: true,
            left: split.x - halfWidth,
            right: split.x + split.width + halfWidth,
            top,
            x: split.x + split.width / 2,
          }
        : {
            bayId: bay.id,
            bayNo: bay.bayNo,
            bottom,
            isBreak: false,
            left: edge - halfWidth,
            right: edge + halfWidth,
            top,
            x: edge,
          },
    );
  }

  return seams;
};

/** The seam under a world point, or null when the point is on no seam. */
export const findSeamAt = (
  seams: readonly PlanogramSeam[],
  point: ViewportPoint,
): PlanogramSeam | null =>
  seams.find(
    (seam) =>
      point.x >= seam.left &&
      point.x <= seam.right &&
      point.y >= seam.top &&
      point.y <= seam.bottom,
  ) ?? null;
